import { useContext, useId } from "react";
import { Plus } from "lucide-react";
import type {
  CompetenceSaisie,
  ContenuScorecardSaisi,
  SousCompetenceSaisie,
} from "@repo/ent-struct";
import { Button, Card, CardContent, Label, Textarea } from "@repo/ui";
import { deplacer, remplacer, retirer } from "../edition";
import {
  ActionsElement,
  aDesProblemes,
  ChampNombre,
  ChampTexte,
  MessagesProbleme,
  ProblemesContext,
} from "./commun";
import { ListeQuestions, QUESTION_VIDE } from "./EditeurQuestion";

const COMPETENCE_VIDE: CompetenceSaisie = {
  nom: "",
  poids: 0,
  questions: [QUESTION_VIDE],
};

const SOUS_COMPETENCE_VIDE: SousCompetenceSaisie = {
  nom: "",
  poids: 0,
  questions: [QUESTION_VIDE],
};

/** Libellé de niveau, au-dessus du nom : « Compétence 2 », « Sous-compétence 2.1 ». */
const LIBELLE_NIVEAU =
  "text-xs font-semibold uppercase tracking-wide text-charte-accent";

function resume(competence: CompetenceSaisie): string {
  const sous = competence.sousCompetences ?? [];
  const nbQuestions =
    (competence.questions?.length ?? 0) +
    sous.reduce((total, s) => total + s.questions.length, 0);
  const parties = [];
  if (sous.length > 0) {
    parties.push(`${sous.length} sous-compétence${sous.length > 1 ? "s" : ""}`);
  }
  parties.push(`${nbQuestions} question${nbQuestions > 1 ? "s" : ""}`);
  return parties.join(" · ");
}

/**
 * Sous-compétence : un filet vertical, pas une carte — elle appartient à la
 * compétence au-dessus, elle n'est pas un bloc du même rang.
 */
function EditeurSousCompetence({
  sous,
  chemin,
  numero,
  onChange,
  actions,
}: {
  sous: SousCompetenceSaisie;
  chemin: string;
  /** « 2.1 » */
  numero: string;
  onChange: (sous: SousCompetenceSaisie) => void;
  actions: React.ReactNode;
}) {
  const problemes = useContext(ProblemesContext);
  return (
    <li className="border-l-4 border-charte-wash-accent-strong pl-4">
      <div className="mb-3 flex items-end gap-3">
        <ChampTexte
          label={`Sous-compétence ${numero}`}
          classeLibelle="text-xs font-medium text-muted-foreground"
          classeChamp="font-medium"
          className="flex-1"
          valeur={sous.nom}
          onChange={(nom) => onChange({ ...sous, nom })}
        />
        <ChampNombre
          label="Poids"
          className="w-24"
          pas={0.05}
          valeur={sous.poids}
          invalide={aDesProblemes(problemes, chemin)}
          onChange={(poids) => onChange({ ...sous, poids })}
        />
        {actions}
      </div>
      <MessagesProbleme chemin={chemin} />
      <ListeQuestions
        questions={sous.questions}
        chemin={chemin}
        onChange={(questions) => onChange({ ...sous, questions })}
      />
    </li>
  );
}

/**
 * Une compétence porte SOIT des questions, SOIT des sous-compétences
 * (`validerScorecard`). Tant qu'elle n'a ni l'un ni l'autre, les deux ajouts
 * sont proposés ; ensuite seul celui de sa forme reste.
 *
 * En-tête en bandeau teinté : c'est le niveau qui structure la grille (et
 * porte le poids qui compte dans le score).
 */
function EditeurCompetence({
  competence,
  chemin,
  numero,
  onChange,
  actions,
}: {
  competence: CompetenceSaisie;
  chemin: string;
  numero: number;
  onChange: (competence: CompetenceSaisie) => void;
  actions: React.ReactNode;
}) {
  const problemes = useContext(ProblemesContext);
  const sousCompetences = competence.sousCompetences ?? [];
  const hierarchique = sousCompetences.length > 0;
  const setSous = (liste: SousCompetenceSaisie[]) =>
    onChange({ ...competence, sousCompetences: liste });

  return (
    <li>
      <Card className="gap-0 overflow-hidden py-0">
        <div className="border-b border-charte-line-strong bg-surface-2 px-6 py-4">
          <div className="flex items-end gap-3">
            <span
              aria-hidden
              className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-charte-accent text-sm font-semibold text-white tabular-nums"
            >
              {numero}
            </span>
            <ChampTexte
              label={`Compétence ${numero}`}
              classeLibelle={LIBELLE_NIVEAU}
              classeChamp="text-base font-semibold md:text-base"
              className="flex-1"
              valeur={competence.nom}
              invalide={aDesProblemes(problemes, chemin)}
              onChange={(nom) => onChange({ ...competence, nom })}
            />
            <ChampNombre
              label="Poids"
              className="w-24"
              pas={0.05}
              valeur={competence.poids}
              invalide={aDesProblemes(problemes, chemin)}
              onChange={(poids) => onChange({ ...competence, poids })}
            />
            {actions}
          </div>
          <p className="mt-2 pl-11 text-xs text-muted-foreground">{resume(competence)}</p>
          <MessagesProbleme chemin={chemin} />
        </div>

        <div className="px-6 py-5">
          {hierarchique ? (
            <div className="space-y-4">
              <ol className="space-y-6">
                {sousCompetences.map((sous, i) => (
                  <EditeurSousCompetence
                    key={i}
                    sous={sous}
                    chemin={`${chemin}.sousCompetences[${i}]`}
                    numero={`${numero}.${i + 1}`}
                    onChange={(s) => setSous(remplacer(sousCompetences, i, s))}
                    actions={
                      <ActionsElement
                        libelle={`la sous-compétence ${numero}.${i + 1}`}
                        index={i}
                        total={sousCompetences.length}
                        onDeplacer={(sens) => setSous(deplacer(sousCompetences, i, sens))}
                        onSupprimer={() => setSous(retirer(sousCompetences, i))}
                      />
                    }
                  />
                ))}
              </ol>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSous([...sousCompetences, SOUS_COMPETENCE_VIDE])}
              >
                <Plus /> Ajouter une sous-compétence
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <ListeQuestions
                questions={competence.questions ?? []}
                chemin={chemin}
                onChange={(questions) => onChange({ ...competence, questions })}
              />
              {!competence.questions?.length && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onChange({
                      nom: competence.nom,
                      poids: competence.poids,
                      sousCompetences: [SOUS_COMPETENCE_VIDE],
                    })
                  }
                >
                  <Plus /> Organiser en sous-compétences
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>
    </li>
  );
}

/** Formulaire arborescent : en-tête, puis compétence › (sous-compétence) › question › critère. */
export function FormulaireScorecard({
  contenu,
  onChange,
}: {
  contenu: ContenuScorecardSaisi;
  onChange: (contenu: ContenuScorecardSaisi) => void;
}) {
  const idDescription = useId();
  const problemes = useContext(ProblemesContext);
  const { competences } = contenu;
  const setCompetences = (liste: CompetenceSaisie[]) =>
    onChange({ ...contenu, competences: liste });

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-3 pt-6">
          <ChampTexte
            label="Titre"
            valeur={contenu.titre}
            invalide={aDesProblemes(problemes, "titre")}
            onChange={(titre) => onChange({ ...contenu, titre })}
          />
          <MessagesProbleme chemin="titre" />
          <div>
            <Label htmlFor={idDescription} className="mb-1 text-xs text-muted-foreground">
              Description
            </Label>
            <Textarea
              id={idDescription}
              fieldSizing="content"
              value={contenu.description}
              onChange={(e) => onChange({ ...contenu, description: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      <MessagesProbleme chemin="competences" />
      <ol className="space-y-4">
        {competences.map((competence, i) => (
          <EditeurCompetence
            key={i}
            competence={competence}
            chemin={`competences[${i}]`}
            numero={i + 1}
            onChange={(c) => setCompetences(remplacer(competences, i, c))}
            actions={
              <ActionsElement
                libelle={`la compétence ${i + 1}`}
                index={i}
                total={competences.length}
                onDeplacer={(sens) => setCompetences(deplacer(competences, i, sens))}
                onSupprimer={() => setCompetences(retirer(competences, i))}
              />
            }
          />
        ))}
      </ol>
      <Button
        type="button"
        variant="outline"
        onClick={() => setCompetences([...competences, COMPETENCE_VIDE])}
      >
        <Plus /> Ajouter une compétence
      </Button>
    </div>
  );
}
